import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import type { BenachrichtigungenApi } from '@elbklar/ui';
import { sitzungErneuern, sitzungsStand, zurAnmeldung } from '@/features/auth/sitzung';
import type {
  Customer,
  CustomerCreate,
  CustomerRelatedData,
  Contact,
  ContactCreate,
  Interaction,
  InteractionCreate,
  Partner,
  PartnerCreate,
  PaginatedResponse,
  CustomerStatus,
  InteractionType,
  PartnerType,
  PartnerStatus,
  User,
  ActivityFeedResponse,
  ActivityType,
} from '@/types';

// API base URL - CRM API runs on port 8001
const API_URL = import.meta.env.VITE_API_URL || '/api';
const ACCESS_TOKEN_KEY = 'crm_access_token';

// Create axios instance with interceptors
const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // Send cookies for auth
});

// Anfrage mit Vermerk für die Erneuerung der Sitzung (siehe `bei401`).
type MitSitzung = InternalAxiosRequestConfig & { _sitzungsStand?: number; _wiederholt?: boolean };

// Request interceptor to add auth token
api.interceptors.request.use((config: MitSitzung) => {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Auf welchem Stand der Sitzung die Anfrage losging (siehe `bei401`).
  config._sitzungsStand = sitzungsStand();
  return config;
});

/**
 * 401: Meist ist nur das Access-Cookie abgelaufen (15 min), die Sitzung gilt
 * per Refresh noch. Deshalb erst erneuern und die Anfrage EINMAL wiederholen;
 * zur Anmeldung nur, wenn core die Erneuerung ablehnt oder die Wiederholung
 * erneut 401 bekommt. Gleichzeitige 401er teilen sich einen Refresh
 * (`sitzungErneuern`). Der Refresh selbst läuft über `fetch` an core-api,
 * nie über diesen Client, und kann deshalb nicht selbst hier landen.
 */
async function bei401(error: AxiosError) {
  // Ein altes Bearer-Token aus dem localStorage verdeckte sonst bei der
  // Wiederholung das frische Cookie, sobald dieses fehlt.
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  const config = error.config as MitSitzung | undefined;
  if (!config || config._wiederholt) {
    zurAnmeldung();
    throw error;
  }
  // Seit dem Absenden schon erneuert (die 401 kam nach dem Refresh an):
  // nicht noch einmal erneuern, nur wiederholen.
  const ergebnis = config._sitzungsStand !== sitzungsStand() ? 'erneuert' : await sitzungErneuern();
  if (ergebnis === 'erneuert') {
    config._wiederholt = true;
    // Die Config trägt den alten Bearer-Header schon; der Request-Interceptor
    // setzt ihn nur, wenn noch ein Token im localStorage liegt.
    delete config.headers?.Authorization;
    return api.request(config);
  }
  if (ergebnis === 'abgelehnt') zurAnmeldung();
  // `nicht_erreichbar`: Netz oder core gestört. Die Anmeldeseite hälfe nicht
  // und nähme die Eingaben mit; der Fehler geht an den Aufrufer.
  throw error;
}

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      return bei401(error);
    }
    return Promise.reject(error);
  }
);

// Generic fetch function
async function fetchApi<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await api.request<T>({ url, ...config });
  return response.data;
}

// Users API
export const usersApi = {
  list: (params?: { department?: string }): Promise<User[]> =>
    fetchApi('/users', { params }),
};

// Customers API
export interface LinkedPartnerData {
  id: string;
  name: string;
  partner_number: string;
  partner_type: string;
  objects: unknown[];
  projects: unknown[];
  quotes: unknown[];
  missions: unknown[];
  object_count: number;
  project_count: number;
  quote_count: number;
  mission_count: number;
}

export const customersApi = {
  list: (params?: {
    page?: number;
    per_page?: number;
    search?: string;
    status?: CustomerStatus;
    assigned_to?: string;
    city?: string;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Customer>> =>
    fetchApi('/customers', { params }),

  // Alle Kunden, seitenweise geholt. Die API deckelt per_page bei 100; eine
  // Anfrage mit 200 bekam 422, und weil die Pipeline sie zusammen mit den
  // Anfragen lud, blieb das Board seit dem 23.06.2026 leer (Befund 09.09.).
  listAll: async (): Promise<Customer[]> => {
    const perPage = 100;
    const first = await customersApi.list({ page: 1, per_page: perPage });
    const items = [...first.items];
    for (let page = 2; page <= first.pages; page++) {
      const next = await customersApi.list({ page, per_page: perPage });
      items.push(...next.items);
    }
    return items;
  },

  get: (id: string): Promise<Customer> => fetchApi(`/customers/${id}`),

  getRelated: (id: string): Promise<CustomerRelatedData> =>
    fetchApi(`/customers/${id}/related`),

  getLinkedPartner: (id: string): Promise<LinkedPartnerData | null> =>
    fetchApi(`/customers/${id}/linked-partner`),

  getActivityFeed: (
    id: string,
    params?: { limit?: number; type?: ActivityType | '' }
  ): Promise<ActivityFeedResponse> =>
    fetchApi(`/customers/${id}/activity-feed`, { params }),

  create: (data: CustomerCreate): Promise<Customer> =>
    fetchApi('/customers', { method: 'POST', data }),

  update: (id: string, data: Partial<CustomerCreate>): Promise<Customer> =>
    fetchApi(`/customers/${id}`, { method: 'PATCH', data }),

  delete: (id: string): Promise<void> =>
    fetchApi(`/customers/${id}`, { method: 'DELETE' }),

  /**
   * Was am Kunden hängt, bevor er zurück zum Lead geht. Füllt die Rückfrage;
   * ändert nichts.
   */
  rueckwegVorschau: (id: string): Promise<RueckwegVorschau> =>
    fetchApi(`/customers/${id}/revert-to-lead/preview`),

  /**
   * Kunde zurück zum Lead (Gegenstück zu `leadsApi.convert`). Setzt den Status
   * auf „Kontaktiert" und leert das Aktivierungsdatum; Kundennummer und alles
   * Verknüpfte bleiben.
   */
  zurueckZumLead: (id: string): Promise<Customer> =>
    fetchApi(`/customers/${id}/revert-to-lead`, { method: 'POST' }),
};

/** Antwort von `GET /customers/{id}/revert-to-lead/preview`. */
export interface RueckwegVorschau {
  anfragen: number;
  projekte: number;
  angebote: number;
  angebote_versandt: number;
  gebaeude: number;
  kontakte: number;
  interaktionen: number;
  customer_number: string;
  status: CustomerStatus;
  ziel_status: CustomerStatus;
  /** `activated_at` ist gesetzt und wird geleert. */
  aktivierung_wird_geleert: boolean;
  /**
   * Der Satz steht **heute** im Zähler der Umwandlungsquote: Status „won" und
   * `activated_at` gesetzt (`services/dashboard.py`, `total_converted`). Ein
   * Kunde in `quote_sent` stand dort nie — ihm zu sagen, er zähle „dann nicht
   * mehr", wäre eine Folge, die nicht eintritt.
   */
  zaehlt_als_umwandlung: boolean;
}

// Contacts API
export const contactsApi = {
  list: (params?: {
    page?: number;
    per_page?: number;
    search?: string;
    customer_id?: string;
    partner_id?: string;
  }): Promise<PaginatedResponse<Contact>> =>
    fetchApi('/contacts', { params }),

  get: (id: string): Promise<Contact> => fetchApi(`/contacts/${id}`),

  create: (data: ContactCreate): Promise<Contact> =>
    fetchApi('/contacts', { method: 'POST', data }),

  update: (id: string, data: Partial<ContactCreate>): Promise<Contact> =>
    fetchApi(`/contacts/${id}`, { method: 'PATCH', data }),

  delete: (id: string): Promise<void> =>
    fetchApi(`/contacts/${id}`, { method: 'DELETE' }),

  setPrimary: (id: string): Promise<Contact> =>
    fetchApi(`/contacts/${id}/primary`, { method: 'PATCH' }),
};

// Interactions API
export const interactionsApi = {
  list: (params?: {
    page?: number;
    per_page?: number;
    search?: string;
    customer_id?: string;
    partner_id?: string;
    contact_id?: string;
    type?: InteractionType;
  }): Promise<PaginatedResponse<Interaction>> =>
    fetchApi('/interactions', { params }),

  get: (id: string): Promise<Interaction> => fetchApi(`/interactions/${id}`),

  create: (data: InteractionCreate): Promise<Interaction> =>
    fetchApi('/interactions', { method: 'POST', data }),

  update: (id: string, data: Partial<InteractionCreate>): Promise<Interaction> =>
    fetchApi(`/interactions/${id}`, { method: 'PATCH', data }),

  delete: (id: string): Promise<void> =>
    fetchApi(`/interactions/${id}`, { method: 'DELETE' }),
};

// Partners API
export const partnersApi = {
  list: (params?: {
    page?: number;
    per_page?: number;
    search?: string;
    partner_type?: PartnerType;
    status?: PartnerStatus;
    assigned_to?: string;
    city?: string;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Partner>> =>
    fetchApi('/partners', { params }),

  get: (id: string): Promise<Partner> => fetchApi(`/partners/${id}`),

  getActivityFeed: (
    id: string,
    params?: { limit?: number; type?: ActivityType | '' }
  ): Promise<ActivityFeedResponse> =>
    fetchApi(`/partners/${id}/activity-feed`, { params }),

  create: (data: PartnerCreate): Promise<Partner> =>
    fetchApi('/partners', { method: 'POST', data }),

  update: (id: string, data: Partial<PartnerCreate>): Promise<Partner> =>
    fetchApi(`/partners/${id}`, { method: 'PUT', data }),

  delete: (id: string): Promise<void> =>
    fetchApi(`/partners/${id}`, { method: 'DELETE' }),
};

// Tasks API
export interface Task {
  id: string;
  title: string;
  description?: string;
  due_date?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in_progress' | 'completed' | 'cancelled' | 'blocked';
  customer_id?: string;
  partner_id?: string;
  assigned_to?: string;
  completed_at?: string;
  created_at: string;
  updated_at?: string;
}

export interface TaskCreate {
  title: string;
  description?: string;
  // null heisst „kein Datum" — ein leerer String ergibt 422, weil das Backend
  // ``date | None`` erwartet (Audit CRM-3).
  due_date?: string | null;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  customer_id?: string;
  partner_id?: string;
  /** Zweiter Bezug, etwa der Ansprechpartner aus Schnell erfassen auf einer Kontaktseite. */
  contact_id?: string;
}

export const tasksApi = {
  list: (params?: { status?: string }): Promise<Task[]> =>
    fetchApi('/tasks', { params }),

  getMyTasks: (): Promise<Task[]> => fetchApi('/tasks/my-tasks'),

  getOverdue: (): Promise<Task[]> => fetchApi('/tasks/overdue'),

  // Get tasks for an entity with hierarchical visibility
  // - customer: includes tasks from projects and deals
  // - project/deal: direct tasks only
  // - partner: direct tasks only (no customer inheritance)
  getForEntity: (
    entityType: 'customer' | 'project' | 'partner' | 'deal',
    entityId: string,
    includeInherited: boolean = true
  ): Promise<Task[]> =>
    fetchApi(`/tasks/entity/${entityType}/${entityId}`, {
      params: { include_inherited: includeInherited },
    }),

  get: (id: string): Promise<Task> => fetchApi(`/tasks/${id}`),

  create: (data: TaskCreate): Promise<Task> =>
    fetchApi('/tasks', { method: 'POST', data }),

  update: (id: string, data: Partial<TaskCreate & { status?: string }>): Promise<Task> =>
    fetchApi(`/tasks/${id}`, { method: 'PUT', data }),

  delete: (id: string): Promise<void> =>
    fetchApi(`/tasks/${id}`, { method: 'DELETE' }),
};

// Anfragen API (Sales-Pipeline — fixed 5-status kanban)
//
// Status ist ein festes Enum (KEINE konfigurierbaren Stages mehr). Die 5 Werte
// SIND die 5 Kanban-Spalten. Quelle: elbklar_core.models.anfrage.AnfrageStatus.
export type AnfrageStatus =
  | 'NEU'
  | 'IN_BEARBEITUNG'
  | 'IN_PLANUNG'
  | 'GEWONNEN'
  | 'VERLOREN';

// Reihenfolge der Kanban-Spalten. Text und Ton: STATUS von @elbklar/ui.
export const ANFRAGE_STATUS_ORDER: AnfrageStatus[] = [
  'NEU',
  'IN_BEARBEITUNG',
  'IN_PLANUNG',
  'GEWONNEN',
  'VERLOREN',
];

// Diagrammfarben der Anfrage-Status: src/lib/anfrageStatus.ts.

export type AnfrageDringlichkeit =
  | 'langfristig'
  | 'normal'
  | 'schnell'
  | 'dringend'
  | 'sofort';

export interface Anfrage {
  id: string;
  customer_id: string;
  contact_id?: string | null;
  status: AnfrageStatus;
  value?: number | string | null;
  lost_reason?: string | null;
  lead_quelle?: string | null;
  kundenwunsch?: string | null;
  sales_nachricht?: string | null;
  vor_ort_termin_at?: string | null;
  dringlichkeit?: AnfrageDringlichkeit | null;
  project_ref_id?: string | null;
  version: number;
  created_at: string;
  updated_at?: string;
}

export interface AnfrageCreate {
  customer_id: string;
  contact_id?: string;
  value?: number;
  status?: AnfrageStatus;
  lead_quelle?: string;
  kundenwunsch?: string;
  sales_nachricht?: string;
  dringlichkeit?: AnfrageDringlichkeit;
}

/** Was PM aus der Uebergabe gemacht hat; nur beim Uebergang nach IN_PLANUNG. */
export interface LeadHandoffResult {
  anfrage_id: string;
  project_id: string;
  building_id: string | null;
  /** Die Zeichenfolge, mit der ein Mensch das Projekt in PM wiederfindet. */
  project_number: string | null;
  project_status: string;
}

export interface AnfrageTransitionResponse {
  anfrage: Anfrage;
  // Optional: Ein pm-api von vor dem 28.10.2026 nennt keine Projektnummer,
  // und jeder andere Uebergang hat gar keine Uebergabe.
  uebergabe?: LeadHandoffResult | null;
  // Gelungen heisst nicht vollstaendig: Findet PM die Objektadresse nicht,
  // entsteht das Projekt ohne Kartenpunkt, ohne Anfahrt und ohne
  // Luftraumpruefung — und sah bis zum 28.10.2026 genauso gelungen aus wie
  // ein vollstaendiges. Der Grund kommt seitdem mit der Erfolgsantwort.
  hinweis?: string | null;
}

export const anfragenApi = {
  list: (): Promise<Anfrage[]> => fetchApi('/anfragen'),

  get: (id: string): Promise<Anfrage> => fetchApi(`/anfragen/${id}`),

  create: (data: AnfrageCreate): Promise<Anfrage> =>
    fetchApi('/anfragen', { method: 'POST', data }),

  update: (id: string, data: Partial<AnfrageCreate>): Promise<Anfrage> =>
    fetchApi(`/anfragen/${id}`, { method: 'PATCH', data }),

  // Guarded 5-status transition (POST /anfragen/{id}/transitions/{target}).
  // version = Optimistic-Lock-Token aus dem letzten Read.
  transition: (
    id: string,
    target: AnfrageStatus,
    version: number,
    reason?: string,
  ): Promise<AnfrageTransitionResponse> =>
    fetchApi(`/anfragen/${id}/transitions/${target}`, {
      method: 'POST',
      data: { version, reason },
    }),

  // Erlaubte Folge-Status aus dem aktuellen Status (SSoT in Python).
  getAllowedTransitions: (id: string): Promise<{ allowed: AnfrageStatus[] }> =>
    fetchApi(`/anfragen/${id}/transitions/allowed`),

  delete: (id: string): Promise<void> =>
    fetchApi(`/anfragen/${id}`, { method: 'DELETE' }),
};

// Dashboard API
export interface SalesGoalData {
  closed_won_value: number;
  closed_won_count: number;
  closed_lost_count: number;
  conversion_rate: number;
  weighted_pipeline_value: number;
  target_value: number;
  target_date: string;
}

// Top open Anfragen for the "Path to 20k" widget (backend: id/name/value/status/customer_id).
export interface TopDealItem {
  id: string;
  name: string;
  value: number;
  status: AnfrageStatus;
  customer_id: string;
}

export interface LeadSourceData {
  source: string;
  label: string;
  count: number;
}

export interface LeadsWidgetData {
  total_leads: number;
  new_this_week: number;
  total_converted: number;
  conversion_rate: number;
  oldest_lead_days: number;
  by_source: LeadSourceData[];
}

export interface DashboardData {
  tasks: {
    today: Array<{
      id: string;
      title: string;
      due_date?: string;
      priority: string;
      status: string;
      customer_id?: string;
      partner_id?: string;
    }>;
    overdue: Array<{
      id: string;
      title: string;
      due_date?: string;
      priority: string;
      status: string;
      customer_id?: string;
      partner_id?: string;
    }>;
    total_open: number;
  };
  pipeline: {
    // One entry per AnfrageStatus (the 5 fixed columns). Backend emits
    // status/name/value/count; color is derived client-side from status.
    stages: Array<{
      status: AnfrageStatus;
      name: string;
      value: number;
      count: number;
    }>;
    total_value: number;
  };
  recent_activities: Array<{
    id: string;
    interaction_type: string;
    subject: string;
    occurred_at: string;
    customer_id?: string;
    partner_id?: string;
  }>;
  customer_types: Array<{
    type: string;
    count: number;
    label: string;
  }>;
  sales_goal: SalesGoalData;
  top_deals: TopDealItem[];
  leads: LeadsWidgetData;
}

// Geldwerte kommen vom Backend als Decimal, also als JSON-String ("12500.00"),
// wie in allen Elbklar-APIs (Muster finance: Decimal bleibt Decimal, das
// Frontend wandelt am Rand). Bis zum 22.10.2026 deklarierten die Typen oben
// number, und GoalTracker addierte Strings: "4800" + "12500" = "480012500",
// die Balkenbreite wurde NaN % (Audit C6). Hier, an genau einer Stelle, wird
// aus dem Drahtformat das Zahlformat, mit dem die Widgets rechnen.
type Geldstring = string;

interface DashboardWire extends Omit<DashboardData, 'sales_goal' | 'pipeline' | 'top_deals'> {
  sales_goal: Omit<SalesGoalData, 'closed_won_value' | 'weighted_pipeline_value' | 'target_value'> & {
    closed_won_value: Geldstring;
    weighted_pipeline_value: Geldstring;
    target_value: Geldstring;
  };
  pipeline: {
    stages: Array<Omit<DashboardData['pipeline']['stages'][number], 'value'> & { value: Geldstring }>;
    total_value: Geldstring;
  };
  top_deals: Array<Omit<TopDealItem, 'value'> & { value: Geldstring }>;
}

function geld(wert: Geldstring | number): number {
  const zahl = Number(wert);
  return Number.isFinite(zahl) ? zahl : 0;
}

export function dashboardAusWire(wire: DashboardWire): DashboardData {
  return {
    ...wire,
    sales_goal: {
      ...wire.sales_goal,
      closed_won_value: geld(wire.sales_goal.closed_won_value),
      weighted_pipeline_value: geld(wire.sales_goal.weighted_pipeline_value),
      target_value: geld(wire.sales_goal.target_value),
    },
    pipeline: {
      stages: wire.pipeline.stages.map((s) => ({ ...s, value: geld(s.value) })),
      total_value: geld(wire.pipeline.total_value),
    },
    top_deals: wire.top_deals.map((d) => ({ ...d, value: geld(d.value) })),
  };
}

export const dashboardApi = {
  get: async (): Promise<DashboardData> => dashboardAusWire(await fetchApi<DashboardWire>('/dashboard')),
};

// Revenue Entries API
export interface RevenueEntry {
  id: string;
  name: string;
  amount: string;      // Decimal serialized as string by FastAPI
  date: string;        // ISO date
  user_id: string;
  created_at: string;
}

export interface RevenueEntryCreate {
  name: string;
  amount: number;
  date: string;        // ISO date (YYYY-MM-DD)
}

export const revenueEntriesApi = {
  list: (): Promise<RevenueEntry[]> => fetchApi('/revenue-entries'),
  create: (data: RevenueEntryCreate): Promise<RevenueEntry> =>
    fetchApi('/revenue-entries', { method: 'POST', data }),
  delete: (id: string): Promise<void> =>
    fetchApi(`/revenue-entries/${id}`, { method: 'DELETE' }),
};

// Search API
export interface SearchResult {
  id: string;
  /** Leads kommen als `lead` (crm-api seit 22.10.2026), nicht mehr als `customer`. */
  type: 'customer' | 'lead' | 'partner' | 'contact';
  name: string;
  email?: string;
  description?: string;
}

export const searchApi = {
  search: (query: string, type?: SearchResult['type']): Promise<{ query: string; results: SearchResult[]; total: number }> =>
    fetchApi('/search', { params: { q: query, type } }),
};

// Emails API
export interface EmailThread {
  id: string;
  subject: string;
  gmail_thread_id: string | null;
  customer_id: string | null;
  contact_id: string | null;
  project_id: string | null;
  last_message_at: string | null;
  message_count: number;
  is_archived: boolean;
  created_at: string;
}

export interface EmailMessage {
  id: string;
  thread_id: string;
  gmail_message_id: string | null;
  direction: 'inbound' | 'outbound';
  status: string;
  from_email: string;
  from_name: string | null;
  to_emails: string[];
  cc_emails: string[];
  subject: string;
  body_text: string | null;
  body_html: string | null;
  sent_at: string | null;
  received_at: string | null;
  has_attachments: boolean;
  created_at: string;
}

export interface EmailThreadDetail extends EmailThread {
  messages: EmailMessage[];
  customer_name: string | null;
  contact_name: string | null;
}

export interface EmailHistoryEntry {
  id: string;
  thread_id: string;
  direction: 'inbound' | 'outbound';
  subject: string;
  from_email: string;
  from_name: string | null;
  body_preview: string | null;
  sent_at: string | null;
  received_at: string | null;
  has_attachments: boolean;
}

export interface EmailHistoryResponse {
  entries: EmailHistoryEntry[];
  total: number;
  has_more: boolean;
}

export const emailsApi = {
  listThreads: (params?: { page?: number; customer_id?: string; contact_id?: string }): Promise<PaginatedResponse<EmailThread>> =>
    fetchApi('/emails', { params }),

  getThread: (threadId: string): Promise<EmailThreadDetail> =>
    fetchApi(`/emails/thread/${threadId}`),

  getByCustomer: (customerId: string, params?: { limit?: number }): Promise<EmailThread[]> =>
    fetchApi(`/emails/by-customer/${customerId}`, { params }),

  getCustomerHistory: (customerId: string, params?: { limit?: number; offset?: number }): Promise<EmailHistoryResponse> =>
    fetchApi(`/emails/history/customer/${customerId}`, { params }),

  getContactHistory: (contactId: string, params?: { limit?: number; offset?: number }): Promise<EmailHistoryResponse> =>
    fetchApi(`/emails/history/contact/${contactId}`, { params }),
};

// Leads API (leads are customers with lead_* status)
export interface LeadCreate {
  company_name: string;
  acquisition_source?: string;
  email?: string;
  phone?: string;
  notes?: string;
  assigned_to?: string;
  contact_first_name?: string;
  contact_last_name?: string;
  contact_email?: string;
  contact_phone?: string;
  contact_role?: string;
}

export interface LeadConvert {
  customer_business_type?: string;
  industry?: string;
  street?: string;
  postal_code?: string;
  city?: string;
  website?: string;
  vat_number?: string;
  managed_objects_count?: number;
  contact_email?: string;
  contact_role?: string;
  assigned_to?: string;
}

export const leadsApi = {
  list: (params?: {
    page?: number;
    per_page?: number;
    search?: string;
    acquisition_source?: string;
    assigned_to?: string;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Customer>> =>
    fetchApi('/leads', { params }),

  create: (data: LeadCreate): Promise<Customer> =>
    fetchApi('/leads', { method: 'POST', data }),

  convert: (leadId: string, data: LeadConvert): Promise<Customer> =>
    fetchApi(`/leads/${leadId}/convert`, { method: 'POST', data }),
};

/**
 * Adapter für das Modul „Benachrichtigungen“ aus `@elbklar/ui`: dieselbe Basis
 * wie alle übrigen Aufrufe (`api` mit Cookie, Bearer-Token, bei 401 erst
 * erneuern, dann zur Anmeldung). Die Pfade (`/notifications…`) setzt das
 * Modul selbst.
 */
export const benachrichtigungenApi: BenachrichtigungenApi = {
  get: <T>(pfad: string, params?: Record<string, string | number>) => fetchApi<T>(pfad, { params }),
  post: <T>(pfad: string, daten?: unknown) => fetchApi<T>(pfad, { method: 'POST', data: daten }),
  put: <T>(pfad: string, daten: unknown) => fetchApi<T>(pfad, { method: 'PUT', data: daten }),
};

// Helper: Extract domain from website URL
export function extractDomainFromWebsite(website: string | null | undefined): string | null {
  if (!website) return null;
  let domain = website.toLowerCase();
  domain = domain.replace(/^https?:\/\//, '').replace(/^www\./, '');
  return domain.split('/')[0] || null;
}

export default api;
